import React, { useEffect, useState, Suspense, lazy } from "react";
import clsx from 'clsx';
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { Container, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import './App.css';
import 'react-notifications/lib/notifications.css';
import { NotificationContainer } from 'react-notifications';
import { configureAxios } from './api/common.service';
import Loader from "./sharedcomponents/Loader/Loader";
import Header from "./sharedcomponents/Header/Header";
import Footer from "./sharedcomponents/Footer/Footer";
import PageLoader from "./sharedcomponents/Widgets/PageLoader";
import { Environment } from "./environments/Environment";
import { drawerWidth } from './common/constants';

// Auth
import { useAuth } from "./common/auth.context";
import SSOLogin from "./sharedcomponents/Login/SSOLogin";

const PREFIX = 'App';

const classes = {
    root: `${PREFIX}-root`,
    drawerHeader: `${PREFIX}-drawerHeader`,
    content: `${PREFIX}-content`,
    contentShift: `${PREFIX}-contentShift`,
    footer: `${PREFIX}-footer`,
    footerShift: `${PREFIX}-footerShift`
};

const Root = styled('span')((
    {
        theme
    }
) => ({
    [`& .${classes.root}`]: {
        paddingTop: theme.spacing(11),
        paddingBottom: theme.spacing(2),
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6),
        minHeight: 300
    },

    [`& .${classes.drawerHeader}`]: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0, 1),
        // necessary for content to be below app bar
        ...theme.mixins.toolbar,
        justifyContent: 'flex-end',
    },

    [`& .${classes.content}`]: {
        flexGrow: 1,
        padding: theme.spacing(3),
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        width: "inherit",
        marginLeft: 0,
        minHeight: 500
    },

    [`& .${classes.contentShift}`]: {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: drawerWidth,
        minHeight: 500
    },

    [`& .${classes.footer}`]: {
        flexGrow: 1,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        width: "inherit",
        marginLeft: 0,
    },

    [`& .${classes.footerShift}`]: {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: drawerWidth,
    }
}));

export function App() {

    const theme = useTheme();

    const { isSignedIn } = useAuth();
    
    useEffect(() => {
        function updatePageTitle() {
            if (Environment.environment)
                document.title = Environment.environment + " - " + document.title;
        }

        configureAxios();
        updatePageTitle();
    }, []);

    const menuStateKey = "menuState";
    const isSmallScreen = window.screen.width < theme.breakpoints.values.sm;
    const getMenuToggleStatus = () => {
        if (localStorage.getItem(menuStateKey) === null) {
            return !isSmallScreen;
        }

        return localStorage.getItem(menuStateKey) === "false" ? false : true;
    }

    const setMenuToggleStatus = (state) => {
        localStorage.setItem(menuStateKey, state);
    }

    const [menuOpen, setMenuOpen] = React.useState(getMenuToggleStatus());

    const handleMenuToggle = (state) => {
        setMenuOpen(state);
        setMenuToggleStatus(state);
    }

    const DashboardLazyComponent = React.lazy(() => import('./views/Dashboard/Dashboard'));
    const AdjustmentManagerLazyComponent = React.lazy(() => import('./views/AdjustmentManager/AdjustmentManager'));
    const ConfigLazyComponent = React.lazy(() => import('./views/ZoneConfiguration/ZoneConfiguration'));
    const CompareLazyComponent = React.lazy(() => import('./views/CompareResult/CompareResult'));
    const LogLazyComponent = React.lazy(() => import('./views/Log/Log'));
    const LenderFilesLazyComponent = React.lazy(() => import('./views/LenderFiles/LenderFiles'))
    const LendersLazyComponent = React.lazy(() => import('./views/Lenders/Lenders'));
    const FileSummaryLazyComponent = React.lazy(() => import('./views/Dashboard/FileSummary/FileSummary'));
    const ManualProcessLazyComponent = React.lazy(() => import('./views/ManualProcess/ManualProcess'));
    const NeedsReviewLazyComponent = React.lazy(() => import('./views/NeedsReview/NeedsReview'));
    const ErrorsLazyComponent = React.lazy(() => import('./views/Errors/Errors'));
    const PriorityZonesLazyComponent = React.lazy(() => import('./views/PriorityZones/PriorityZones'));
    const SyncRatesLazyComponent = React.lazy(() => import('./views/SyncLenderRates/SyncLenderRates'));
    const AdminSettingsLazyComponent = React.lazy(() => import('./views/Admin/Settings/Settings'));
    const AdminUsersLazyComponent = React.lazy(() => import('./views/Admin/Users/Users'));
    const AdminRolesLazyComponent = React.lazy(() => import('./views/Admin/Roles/Roles'));
    const AdminAuditLazyComponent = React.lazy(() => import('./views/Admin/Audit/Audit'));
    const AdminImportExportLazyComponent = React.lazy(() => import('./views/Admin/ImportExport/ImportExport'));

    return (
        <Root>
            {!isSignedIn
             ? (<SSOLogin />)
             : (
                <Router>
                    <Loader />
                    <NotificationContainer />
                    <Header menuOpen={menuOpen} onMenuToggle={handleMenuToggle} drawerWidth={drawerWidth} isAuthenticated={isSignedIn} />

                    <Container maxWidth={false} className={clsx(classes.content, {
                        [classes.contentShift]: menuOpen,
                    })}>
                        <div className={classes.drawerHeader} />
                        <Suspense fallback={<PageLoader />}>
                            <Routes>
                                {<React.Fragment>
                                    <Route exact path="/" element={<DashboardLazyComponent />} />
                                    <Route path="/dashboard" element={<DashboardLazyComponent />} />
                                    <Route path="/adjustmentmanager" element={<AdjustmentManagerLazyComponent />} />
                                    <Route path="/config" element={<ConfigLazyComponent />} />
                                    <Route path="/compare" element={<CompareLazyComponent />} />
                                    <Route path="/log" element={<LogLazyComponent />} />
                                    <Route path="/lenderfiles" element={<LenderFilesLazyComponent />} />
                                    <Route path="/lenders" element={<LendersLazyComponent />} />
                                    <Route path="/filesummary" element={<FileSummaryLazyComponent />} />
                                    <Route path="/manualprocess" element={<ManualProcessLazyComponent />} />
                                    <Route path="/needsreview" element={<NeedsReviewLazyComponent />} />
                                    <Route path="/errors" element={<ErrorsLazyComponent />} />
                                    <Route path="/priorityzones" element={<PriorityZonesLazyComponent />} />
                                    <Route path="/syncrates" element={<SyncRatesLazyComponent />} />
                                    <Route path="/admin/settings" element={<AdminSettingsLazyComponent />} />
                                    <Route path="/admin/users" element={<AdminUsersLazyComponent />} />
                                    <Route path="/admin/roles" element={<AdminRolesLazyComponent />} />
                                    <Route path="/admin/audit" element={<AdminAuditLazyComponent />} />
                                    <Route path="/admin/importexport" element={<AdminImportExportLazyComponent />} />
                                </React.Fragment>}
                            </Routes>
                        </Suspense>
                    </Container>

                    <Footer className={clsx(classes.footer, {
                        [classes.footerShift]: menuOpen,
                    })} />
                </Router>
             )}
        </Root>
    );
}

export default App;